
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __loadNamespaces from 'next-translate/loadNamespaces'
// @ts-ignore
    
import React from "react";
import useTranslation from "next-translate/useTranslation";

import { Head } from "@/components/Head";
import { RequestDemoSection } from "@/sections/RequestDemoSection";
import { useInquiryForm } from "@/hooks/useInquiryForm";
import { SolutionsPageTopSection } from "@/sections/SolutionsPageTopSection";
import { UseCaseSteps } from "@/sections/UseCaseSteps";
import { CALENDLY_LINK_PRODUCT_DEMO } from "@/constants/pricing";
import { ExcitedToJoinSection } from "@/sections/ExcitedToJoinSection";
import { EnterprisePricingSection } from "@/sections/EnterprisePricingSection";
import { EnterpriseGradeSecuritySection } from "@/sections/EnterpriseGradeSecuritySection";
import { EnterpriseSolutionsFaqSection } from "@/sections/EnterpriseSolutionsFaqSection";
import { FormSourceType } from "@/containers/InquiryForm";

declare const Intercom: (...args: unknown[]) => any;

const SolutionsForEnterprise = () => {
  const { t } = useTranslation();
  const { toggleInquiryForm } = useInquiryForm();

  const handleEvent = (message?: string) => {
    if (typeof Intercom !== "undefined") {
      Intercom(
        "showNewMessage",
        message ? t(`navigation:resourcesDropdown.${message}`) : undefined,
      );
    }
  };

  return (
    <>
      <Head
        pageNamespace="page/solutionsForEnterprise"
        ogImage="/images/solutions-for-enterprise.jpg"
      />

      <SolutionsPageTopSection
        title={t("page/solutionsForEnterprise:section/video.title")}
        subtitle={t("page/solutionsForEnterprise:section/video.subtitle")}
        image={{
          src: "/images/layers.png",
          alt: "Magicul layers",
        }}
      />

      <UseCaseSteps
        title={t("page/solutionsForEnterprise:section/useCases.title")}
        hasUseCaseHeaderSection
        steps={[
          {
            id: "STEP#1",
            image: "/images/system-migration.png",
            title: t(
              "page/solutionsForEnterprise:section/useCases.steps.systemMigration.title",
            ),
            text: t(
              "page/solutionsForEnterprise:section/useCases.steps.systemMigration.text",
            ),
            rightSidedImage: true,
          },
          {
            id: "STEP#2",
            image: "/images/bulk-migration.png",
            title: t(
              "page/solutionsForEnterprise:section/useCases.steps.bulkMigration.title",
            ),
            text: t(
              "page/solutionsForEnterprise:section/useCases.steps.bulkMigration.text",
            ),
          },
          {
            id: "STEP#3",
            image: "/images/automated-migration.png",
            title: t(
              "page/solutionsForEnterprise:section/useCases.steps.automatedMigration.title",
            ),
            text: t(
              "page/solutionsForEnterprise:section/useCases.steps.automatedMigration.text",
            ),
            rightSidedImage: true,
          },
        ]}
      />
      <ExcitedToJoinSection
        title={t("page/solutionsForEnterprise:section/excitedToJoin.title")}
        text={t("page/solutionsForEnterprise:section/excitedToJoin.text")}
        rightSectionText={t(
          "page/solutionsForEnterprise:section/excitedToJoin.rightSectionText",
        )}
        buttonLabel={t("common:contactUs")}
        onButtonClick={() => handleEvent("contactUs")}
        wrapperBgColor="#F7F8FD"
      />

      <EnterprisePricingSection
        title={t("page/solutionsForEnterprise:section/pricing.title")}
        subtitle={t("page/solutionsForEnterprise:section/pricing.subtitle")}
        cards={[
          {
            id: 0,
            image: {
              src: "/images/file-converter-image.png",
              alt: "design file converter",
            },
            name: t(
              "page/solutionsForEnterprise:section/pricing.cards.fileConverter.name",
            ),
            startingAt: t(
              "page/solutionsForEnterprise:section/pricing.cards.fileConverter.startingAt",
            ),
            price: 2700,
            text: t(
              "page/solutionsForEnterprise:section/pricing.cards.fileConverter.text",
            ),
          },
          {
            id: 1,
            image: {
              src: "/images/figma-backup-tool-image.png",
              alt: "figma backup tool",
            },
            name: t(
              "page/solutionsForEnterprise:section/pricing.cards.figmaBackup.name",
            ),
            startingAt: t(
              "page/solutionsForEnterprise:section/pricing.cards.figmaBackup.startingAt",
            ),
            price: 119,
            text: t(
              "page/solutionsForEnterprise:section/pricing.cards.figmaBackup.text",
            ),
          },
        ]}
      />

      <EnterpriseGradeSecuritySection
        title={t(
          "page/solutionsForEnterprise:section/enterpriseSecurity.title",
        )}
        cards={[
          {
            id: 0,
            title: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.dataEncryption.title",
            ),
            text: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.dataEncryption.text",
            ),
            icon: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.dataEncryption.icon",
            ),
          },
          {
            id: 1,
            title: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.authentication.title",
            ),
            text: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.authentication.text",
            ),
            icon: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.authentication.icon",
            ),
          },
          {
            id: 2,
            title: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.dataHosting.title",
            ),
            text: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.dataHosting.text",
            ),
            icon: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.dataHosting.icon",
            ),
          },
          {
            id: 3,
            title: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.premiumSupport.title",
            ),
            text: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.premiumSupport.text",
            ),
            icon: t(
              "page/solutionsForEnterprise:section/enterpriseSecurity.list.premiumSupport.icon",
            ),
          },
        ]}
      />

      <EnterpriseSolutionsFaqSection />

      <RequestDemoSection
        title={t("section/requestDemo:title")}
        subtitle={t("section/requestDemo:subtitle")}
        buttonLabel={t("section/requestDemo:buttonLabel")}
        wrapperBgColor="#F7F8FD"
        onButtonClick={() =>
          toggleInquiryForm(FormSourceType.FOR_ENTERPRISES_INQUIRY)
        }
        requestDemoLink={CALENDLY_LINK_PRODUCT_DEMO}
      />
    </>
  );
};

export default SolutionsForEnterprise;


// @ts-ignore
    export async function getStaticProps(ctx) {
// @ts-ignore
        
// @ts-ignore
        
// @ts-ignore
        return {
// @ts-ignore
          
// @ts-ignore
          
// @ts-ignore
          props: {
// @ts-ignore
            
// @ts-ignore
            ...(await __loadNamespaces({
// @ts-ignore
              ...ctx,
// @ts-ignore
              pathname: '/solutions/for-enterprise',
// @ts-ignore
              loaderName: 'getStaticProps',
// @ts-ignore
              ...__i18nConfig,
// @ts-ignore
              
// @ts-ignore
            }))
// @ts-ignore
          }
// @ts-ignore
        }
// @ts-ignore
    }
// @ts-ignore
  